import {
  array,
  number,
  object,
  ref,
  string,
  StringSchema,
  NumberSchema,
} from 'yup';
import { help, regularExpressions } from 'src/utils/properties.utils';
import { GRNFormItem } from 'src/models/Management.interface';

export const loginValidationSchema = object({
  email: string()
    .trim()
    .email(help.INVALID_EMAIL_ADDRESS)
    .required('Email ' + help.REQUIRED),
  password: string()
    .trim()
    .required('Password ' + help.REQUIRED),
});

export const signupEmailSchema = object({
  email: string()
    .trim()
    .email(help.INVALID_EMAIL_ADDRESS)
    .matches(regularExpressions.EMAIL_REGEX, help.INVALID_EMAIL_ADDRESS)
    .required('Email ' + help.REQUIRED),
});

export const signupCompanySchema = object({
  companyName: string()
    .trim()
    .required('Company Name ' + help.REQUIRED),
  companyReg: string()
    .trim()
    .required('Registration No. ' + help.REQUIRED),
  companyCountry: string()
    .trim()
    .required('Country ' + help.REQUIRED),
  addressLine1: string()
    .trim()
    .required('Address Line 1 ' + help.REQUIRED),
  addressLine2: string()
    .trim()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  postalCode: string()
    .trim()
    .max(20, 'Postal Code must be at most 20 characters')
    .required('Postal Code ' + help.REQUIRED),
  city: string()
    .trim()
    .required('City ' + help.REQUIRED),
});

export const signupProfileSchema = object({
  firstName: string()
    .trim()
    .required('First Name ' + help.REQUIRED),
  lastName: string()
    .trim()
    .required('Last Name ' + help.REQUIRED),
  phoneNumber: string()
    .trim()
    .required('Phone ' + help.REQUIRED)
    .matches(/^\d{6,10}$/, help.INVALID_PHONE_NUMBER),
  password: string()
    .trim()
    .required('Password ' + help.REQUIRED)
    .min(8, help.PASSWORD_MIN)
    .matches(/[a-z]/, help.PASSWORD_LOWERCASE)
    .matches(/[A-Z]/, help.PASSWORD_UPPERCASE)
    .matches(/[0-9]/, help.PASSWORD_INTEGER)
    .matches(/[@$!%*?&#]/, help.PASSWORD_SPECIAL_CHARACTER),
  repassword: string()
    .trim()
    .required('Password ' + help.REQUIRED)
    .oneOf([ref('password')], help.PASSWORD_MISMATCH),
});

export const signupValidationSchema = [
  signupEmailSchema,
  signupCompanySchema,
  signupProfileSchema,
];

export const userValidationSchema = object({
  firstName: string()
    .trim()
    .matches(/^[A-Za-z\s]+$/, help.INVALID_FORMAT)
    .required('First Name ' + help.REQUIRED),
  lastName: string()
    .trim()
    .matches(/^[A-Za-z\s]+$/, help.INVALID_FORMAT)
    .required('Last Name ' + help.REQUIRED),
  email: string()
    .email(help.INVALID_EMAIL_ADDRESS)
    .trim()
    .required('Email ' + help.REQUIRED),
  role: array()
    .of(string())
    .min(1, 'Role ' + help.REQUIRED),
});

export const vendorValidationSchema = object({
  organizationName: string()
    .trim()
    .required('Organization ' + help.REQUIRED),
  address: string()
    .trim()
    .required('Address ' + help.REQUIRED),
  contactPersonName: string()
    .trim()
    .matches(/^[A-Za-z\s]+$/, help.INVALID_FORMAT)
    .required('Point of Contact ' + help.REQUIRED),
  phoneNumber: string()
    .trim()
    .required('Contact Number ' + help.REQUIRED)
    .matches(/^\d{6,10}$/, help.INVALID_PHONE_NUMBER),
  email: string()
    .email(help.INVALID_EMAIL_ADDRESS)
    .trim()
    .required('Email ' + help.REQUIRED),
});

export const rawMaterialValidationSchema = object({
  name: string()
    .trim()
    .required('Name ' + help.REQUIRED),
  code: string()
    .trim()
    .required('Code ' + help.REQUIRED),
  minLevelQty: number()
    .required('Minimum Quanitity Level ' + help.REQUIRED)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  unit: string()
    .trim()
    .required('Unit ' + help.REQUIRED),
  unitPrice: number()
    .required('Unit Price ' + help.REQUIRED)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  rawMaterialCategoryId: string().nullable(),
});

export const rawMaterialCategoryValidationSchema = object({
  name: string()
    .trim()
    .required('Category Name ' + help.REQUIRED),
});

export const productValidation = object({
  name: string()
    .trim()
    .required('Name ' + help.REQUIRED),
  displayOrder: number()
    .required('Display Order ' + help.REQUIRED)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  unit: string()
    .trim()
    .required('Unit ' + help.REQUIRED),
  unitPrice: number()
    .required('Unit Price ' + help.REQUIRED)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
});

export const resetQuantityValidation = object({
  quantity: number()
    .required('Available Quantity ' + help.REQUIRED)
    .min(0, 'Quantity must be zero or a positive number')
    .integer('Quantity must be a whole number')
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
});

export const bankValidationSchema = object({
  bankName: string()
    .trim()
    .required('Bank Name ' + help.REQUIRED),
  branchName: string()
    .trim()
    .required('Branch Name ' + help.REQUIRED),
  accountName: string()
    .trim()
    .required('Account Holder’s Name ' + help.REQUIRED),
  accountNumber: string()
    .trim()
    .matches(/^\d+$/, help.INVALID_FORMAT)
    .required('Account Number ' + help.REQUIRED),
  accountType: string()
    .trim()
    .required('Account Type ' + help.REQUIRED),
});

export const voucherTypeValidationSchema = object({
  name: string()
    .trim()
    .required('Voucher Type Name ' + help.REQUIRED),
  displayOrder: number()
    .required('Display Order ' + help.REQUIRED)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  description: string()
    .trim()
    .required('Description ' + help.REQUIRED),
});

export const discountValidationSchema = object({
  name: string()
    .trim()
    .required('Discount Name ' + help.REQUIRED),
  description: string()
    .trim()
    .required('Description ' + help.REQUIRED),
  percentage: number()
    .min(0.01, help.MIN_VALUE_REQUIRED)
    .nullable()
    .transform((value: any, originalValue: string | null) => {
      if (originalValue === '' || originalValue === null) {
        return null;
      }
      return value;
    })
    .max(100, help.CANNOT_EXCEED_100)
    .typeError(help.INVALID_FORMAT),
  defaultValue: number()
    .min(0.01, help.MIN_VALUE_REQUIRED)
    .nullable()
    .transform((value: any, originalValue: string | null) => {
      if (originalValue === '' || originalValue === null) {
        return null;
      }
      return value;
    })
    .typeError(help.INVALID_FORMAT),
})
  .test(
    'percentage-or-defaultValue',
    'Provide either a percentage or a default value, but not both',
    function (values: {
      percentage?: number | null;
      defaultValue?: number | null;
    }) {
      const { percentage, defaultValue } = values;
      if ((percentage && !defaultValue) || (!percentage && defaultValue)) {
        return true;
      }
      return this.createError({
        path: 'percentage',
        message:
          percentage === null && defaultValue === null
            ? help.PERCENTAGE_OR_DEFAULT_REQUIRED
            : help.EITHER_PERCENTAGE_OR_DEFAULT_ONLY,
      });
    }
  )
  .test(
    'percentage-or-defaultValue-default',
    'Provide either a percentage or a default value, but not both',
    function (values: {
      percentage?: number | null;
      defaultValue?: number | null;
    }) {
      const { percentage, defaultValue } = values;
      if ((percentage && !defaultValue) || (!percentage && defaultValue)) {
        return true;
      }

      return this.createError({
        path: 'defaultValue',
        message:
          defaultValue === null && percentage === null
            ? help.PERCENTAGE_OR_DEFAULT_REQUIRED
            : help.EITHER_PERCENTAGE_OR_DEFAULT_ONLY,
      });
    }
  );

export const surchargeValidationSchema = object({
  name: string()
    .trim()
    .required('Surcharge Name ' + help.REQUIRED),
  description: string()
    .trim()
    .required('Description ' + help.REQUIRED),
  percentage: number()
    .min(0.01, help.MIN_VALUE_REQUIRED)
    .nullable()
    .transform((value: any, originalValue: string | null) => {
      if (originalValue === '' || originalValue === null) {
        return null;
      }
      return value;
    })
    .max(100, help.CANNOT_EXCEED_100)
    .typeError(help.INVALID_FORMAT),
  defaultValue: number()
    .min(0.01, help.MIN_VALUE_REQUIRED)
    .nullable()
    .transform((value: any, originalValue: string | null) => {
      if (originalValue === '' || originalValue === null) {
        return null;
      }
      return value;
    })
    .typeError(help.INVALID_FORMAT),
})
  .test(
    'percentage-or-defaultValue',
    'Provide either a percentage or a default value, but not both',
    function (values: {
      percentage?: number | null;
      defaultValue?: number | null;
    }) {
      const { percentage, defaultValue } = values;
      if ((percentage && !defaultValue) || (!percentage && defaultValue)) {
        return true;
      }
      return this.createError({
        path: 'percentage',
        message:
          (percentage === null || percentage === undefined) &&
          (defaultValue === null || defaultValue === undefined)
            ? help.PERCENTAGE_OR_DEFAULT_REQUIRED
            : help.EITHER_PERCENTAGE_OR_DEFAULT_ONLY,
      });
    }
  )
  .test(
    'percentage-or-defaultValue-default',
    'Provide either a percentage or a default value, but not both',
    function (values: {
      percentage?: number | null;
      defaultValue?: number | null;
    }) {
      const { percentage, defaultValue } = values;
      if ((percentage && !defaultValue) || (!percentage && defaultValue)) {
        return true;
      }

      return this.createError({
        path: 'defaultValue',
        message:
          (defaultValue === null || defaultValue === undefined) &&
          (percentage === null || percentage === undefined)
            ? help.PERCENTAGE_OR_DEFAULT_REQUIRED
            : help.EITHER_PERCENTAGE_OR_DEFAULT_ONLY,
      });
    }
  );

export const customerValidationSchema = object({
  name: string()
    .trim()
    .required('Name ' + help.REQUIRED),
  customerCode: string()
    .trim()
    .required('Code ' + help.REQUIRED),
  organizationName: string()
    .trim()
    .required('Organization ' + help.REQUIRED),
  telephoneNo: string()
    .trim()
    .required('Telephone Number ' + help.REQUIRED)
    .matches(/^\d{6,10}$/, help.INVALID_PHONE_NUMBER),
  email: string().trim().email(help.INVALID_EMAIL_ADDRESS),
});

export const customerAddressValidationSchema = object({
  contactPersonName: string()
    .trim()
    .required('Contact person name ' + help.REQUIRED),
  contactNo: string()
    .trim()
    .required('Contact Number ' + help.REQUIRED)
    .matches(/^\d{6,10}$/, help.INVALID_PHONE_NUMBER),
  addressLine1: string()
    .trim()
    .required('Address ' + help.REQUIRED),
  city: string()
    .trim()
    .required('City ' + help.REQUIRED),
  postCode: string()
    .trim()
    .required('Post code ' + help.REQUIRED),
  country: string()
    .trim()
    .required('Country ' + help.REQUIRED),
});

export const purchaseOrderValidation = (
  items?: { id?: string; rawMaterialId?: string; requestedQty?: string }[]
) => {
  const dynamicFields: Record<string, StringSchema | NumberSchema> = {};

  if (items && items.length > 0) {
    items.forEach((item) => {
      dynamicFields[`rawMaterialId-${item.id}`] = string().required(
        help.RAW_MATERIAL_REQUIRED
      );

      dynamicFields[`requestedQty-${item.id}`] = number()
        .required(help.QUANTITY_REQUIRED)
        .min(0.01, help.QUANTITY_MIN)
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }
          return value;
        });
    });
  }

  return object({
    vendor: string().required('Vendor ' + help.REQUIRED),
    deliveryAddress: string().required('Delivery address ' + help.REQUIRED),
    ...dynamicFields,
  });
};

export const grnValidation = (items?: GRNFormItem[]) => {
  const dynamicFields: Record<string, NumberSchema | StringSchema> = {};
  if (items && items.length > 0) {
    items.forEach((item) => {
      dynamicFields[`rawMaterialId-${item.id}`] = string().required(
        'Item ' + help.REQUIRED
      );
      dynamicFields[`receivedQty-${item.id}`] = number()
        .required(help.FIELD_REQUIRED)
        .min(0.01, '0 not allowed')
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }
          return value;
        })
        .typeError(help.INVALID_FORMAT);
      dynamicFields[`returnedQty-${item.id}`] = number()
        .required(help.FIELD_REQUIRED)
        .min(0, 'Cannot be negative')
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return 0;
          }
          return value;
        })
        .test(
          'max-returned-qty',
          'Should be less than to rec. qty',
          function (value) {
            const receivedQty = this.parent[`receivedQty-${item.id}`];
            if (!receivedQty || !value) return true;
            return value <= receivedQty;
          }
        )
        .typeError(help.INVALID_FORMAT);
      dynamicFields[`unitPrice-${item.id}`] = number()
        .required(help.FIELD_REQUIRED)
        .min(0.01, '0 not allowed')
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }
          return value;
        })
        .typeError(help.INVALID_FORMAT);
    });
  }

  return object({
    vendor: string().required('Vendor ' + help.REQUIRED),
    invoiceNumber: string().required('Invoice Number ' + help.REQUIRED),
    invoiceAmount: number()
      .required('Invoice Amount ' + help.REQUIRED)
      .positive('Invoice Amount must be greater than 0')
      .typeError('Invoice Amount ' + help.INVALID_FORMAT),
    ...dynamicFields,
  });
};

export const grnFilterValidation = object({
  startDate: string().nullable(),
  endDate: string()
    .nullable()
    .test(
      'end-date-after-start-date',
      'End date must be the same as or later than the start date.',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        return new Date(value) >= new Date(startDate);
      }
    ),
});

export const profileValidationSchema = object({
  firstName: string()
    .trim()
    .required('First Name ' + help.REQUIRED),
  lastName: string()
    .trim()
    .required('Last Name ' + help.REQUIRED),
  phoneNumber: string()
    .trim()
    .required('Mobile Number ' + help.REQUIRED)
    .matches(/^\d{6,10}$/, help.INVALID_PHONE_NUMBER),
});

export const profilePasswordValidationSchema = object({
  currentPassword: string()
    .trim()
    .required('Current Password ' + help.REQUIRED),
  newPassword: string()
    .trim()
    .required('New Password ' + help.REQUIRED)
    .min(8, help.PASSWORD_MIN)
    .matches(/[a-z]/, help.PASSWORD_LOWERCASE)
    .matches(/[A-Z]/, help.PASSWORD_UPPERCASE)
    .matches(/[0-9]/, help.PASSWORD_INTEGER)
    .matches(/[@$!%*?&#]/, help.PASSWORD_SPECIAL_CHARACTER),
  confirmPassword: string()
    .trim()
    .required('Confirm Password ' + help.REQUIRED)
    .oneOf([ref('newPassword')], help.PASSWORD_MISMATCH),
});

export const resetPasswordValidationSchema = object({
  newPassword: string()
    .trim()
    .required('New Password ' + help.REQUIRED)
    .min(8, help.PASSWORD_MIN)
    .matches(/[a-z]/, help.PASSWORD_LOWERCASE)
    .matches(/[A-Z]/, help.PASSWORD_UPPERCASE)
    .matches(/[0-9]/, help.PASSWORD_INTEGER)
    .matches(/[@$!%*?&#]/, help.PASSWORD_SPECIAL_CHARACTER),
  confirmPassword: string()
    .trim()
    .required('Confirm Password ' + help.REQUIRED)
    .oneOf([ref('newPassword')], help.PASSWORD_MISMATCH),
});

export const companyValidationSchema = object({
  companyName: string()
    .trim()
    .required('Company Name ' + help.REQUIRED),
  companyRegNo: string()
    .trim()
    .required('Company Registration Number ' + help.REQUIRED),
  country: string()
    .trim()
    .required('Country ' + help.REQUIRED),
  addressLine1: string()
    .trim()
    .required('Address Line 1 ' + help.REQUIRED),
  addressLine2: string()
    .trim()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  postalCode: string()
    .trim()
    .max(20, 'Postal Code must be at most 20 characters')
    .required('Postal Code ' + help.REQUIRED),
  city: string()
    .trim()
    .required('City ' + help.REQUIRED),
  telephoneNumber: string()
    .trim()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  websiteUrl: string()
    .trim()
    .matches(regularExpressions.URL_REGEX, help.INVALID_URL)
    .nullable()
    .transform((value) => (value === '' ? null : value)),
});

export const productionMappingValidation = (items: { id: string }[]) => {
  const dynamicFields: Record<string, StringSchema | NumberSchema> = {};

  items.forEach((item) => {
    dynamicFields[`rawMaterialId-${item.id}`] = string().required(
      help.RAW_MATERIAL_REQUIRED
    );

    dynamicFields[`quantity-${item.id}`] = number()
      .required(help.QUANTITY_REQUIRED)
      .min(0.01, help.QUANTITY_MIN)
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return undefined;
        }
        return value;
      });
  });

  return object({
    productId: string().required(help.PRODUCT_REQUIRED),
    ...dynamicFields,
  });
};

export const grnPaymentValidation = object({
  receivedBy: string().required('Received By ' + help.REQUIRED),
  paymentDate: string().required('Payment Date ' + help.REQUIRED),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0'),
  paymentMode: number().required('Payment Method ' + help.REQUIRED),
});

export const grnPaymentChequeValidation = object({
  receivedBy: string().required('Received By ' + help.REQUIRED),
  paymentDate: string().required('Payment Date ' + help.REQUIRED),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0'),
  paymentMode: number().required('Payment Method ' + help.REQUIRED),
  chequeNumber: string().required('Cheque Number ' + help.REQUIRED),
  chequeDate: string().required('Cheque Date ' + help.REQUIRED),
  bankName: string().required('Bank Name ' + help.REQUIRED),
});

export const grnPaymentOnlineValidation = object({
  receivedBy: string().required('Received By ' + help.REQUIRED),
  paymentDate: string().required('Payment Date ' + help.REQUIRED),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0'),
  paymentMode: number().required('Payment Method ' + help.REQUIRED),
  transactionReference: string().required(
    'Transaction Reference ' + help.REQUIRED
  ),
});

// GRN Payment Validations - Dynamic schema that handles all payment modes
export const grnPaymentDynamicValidation = (remainingAmount?: number) =>
  object({
    receivedBy: string()
      .trim()
      .matches(/^[A-Za-z\s]+$/, help.TEXT_ONLY)
      .required('Received By ' + help.REQUIRED),
    paymentDate: string()
      .required('Payment Date ' + help.REQUIRED)
      .test(
        'not-future-date',
        help.PAYMENT_DATE_CANNOT_BE_FUTURE,
        function (value) {
          if (!value) return true;
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          return selectedDate <= today;
        }
      ),
    amount: number()
      .required('Amount ' + help.REQUIRED)
      .positive('Amount must be greater than 0')
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          return originalValue.trim() === '' ? undefined : value;
        }
        return originalValue === null || originalValue === undefined
          ? undefined
          : value;
      })
      .typeError(help.INVALID_FORMAT)
      .test(
        'max-remaining-amount',
        `Amount exceeds remaining payable amount${
          remainingAmount ? ` (${remainingAmount.toLocaleString()})` : ''
        }`,
        function (value) {
          if (!remainingAmount || !value) return true;
          return value <= remainingAmount;
        }
      ),
    paymentMode: number()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return undefined;
        }
        return value;
      })
      .required('Payment Method ' + help.REQUIRED)
      .typeError('Payment Method ' + help.REQUIRED),
    // Cheque fields - required when paymentMode is 2 (CHEQUE)
    chequeNumber: string().when('paymentMode', {
      is: 2,
      then: (schema) =>
        schema
          .trim()
          .matches(/^\d+$/, help.INVALID_FORMAT)
          .required('Cheque Number ' + help.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
    chequeDate: string().when('paymentMode', {
      is: 2,
      then: (schema) => schema.required('Cheque Date ' + help.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
    // Bank Account - required when paymentMode is 2 (CHEQUE) or 3 (ONLINE)
    bankAccountId: string().when('paymentMode', {
      is: (val: number) => val === 2 || val === 3,
      then: (schema) => schema.required('Bank Account ' + help.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
    // Transaction Reference - required when paymentMode is 3 (ONLINE)
    transactionReference: string().when('paymentMode', {
      is: 3,
      then: (schema) =>
        schema.required('Transaction Reference ' + help.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

// Order Payment Validations - Dynamic schema that handles all payment modes
export const orderPaymentDynamicValidation = object({
  paymentDate: string()
    .required('Payment Date ' + help.REQUIRED)
    .test(
      'not-future-date',
      help.PAYMENT_DATE_CANNOT_BE_FUTURE,
      function (value) {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return selectedDate <= today;
      }
    ),
  amount: string()
    .required('Amount ' + help.REQUIRED)
    .test('is-valid-amount', 'Amount must be greater than 0', function (value) {
      if (!value) return false;
      const numValue = parseFloat(value.replace(/,/g, ''));
      return !isNaN(numValue) && numValue > 0;
    }),
  paymentMode: number()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .required('Payment Method ' + help.REQUIRED)
    .typeError('Payment Method ' + help.REQUIRED),
  // Cheque fields - required when paymentMode is 2 (CHEQUE)
  chequeNumber: string().when('paymentMode', {
    is: 2,
    then: (schema) => schema.required('Cheque Number ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  chequeDate: string().when('paymentMode', {
    is: 2,
    then: (schema) => schema.required('Cheque Date ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  // Bank Account - required when paymentMode is 2 (CHEQUE) or 3 (ONLINE)
  bankAccountId: string().when('paymentMode', {
    is: (val: number) => val === 2 || val === 3,
    then: (schema) => schema.required('Bank Account ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  // Transaction Reference - required when paymentMode is 3 (ONLINE)
  transactionReference: string().when('paymentMode', {
    is: 3,
    then: (schema) => schema.required('Transaction Reference ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// Keep the old schemas for backward compatibility
export const orderPaymentValidation = object({
  paymentDate: string()
    .required('Payment Date ' + help.REQUIRED)
    .test(
      'not-future-date',
      help.PAYMENT_DATE_CANNOT_BE_FUTURE,
      function (value) {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return selectedDate <= today;
      }
    ),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0')
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  paymentMode: number()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .required('Payment Method ' + help.REQUIRED)
    .typeError('Payment Method ' + help.REQUIRED),
});

export const orderPaymentChequeValidation = object({
  paymentDate: string()
    .required('Payment Date ' + help.REQUIRED)
    .test(
      'not-future-date',
      help.PAYMENT_DATE_CANNOT_BE_FUTURE,
      function (value) {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return selectedDate <= today;
      }
    ),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0')
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  paymentMode: number()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .required('Payment Method ' + help.REQUIRED)
    .typeError('Payment Method ' + help.REQUIRED),
  chequeNumber: string().required('Cheque Number ' + help.REQUIRED),
  chequeDate: string().required('Cheque Date ' + help.REQUIRED),
  bankAccountId: string().required('Bank Account ' + help.REQUIRED),
});

export const orderPaymentOnlineValidation = object({
  paymentDate: string()
    .required('Payment Date ' + help.REQUIRED)
    .test(
      'not-future-date',
      help.PAYMENT_DATE_CANNOT_BE_FUTURE,
      function (value) {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return selectedDate <= today;
      }
    ),
  amount: number()
    .required('Amount ' + help.REQUIRED)
    .positive('Amount must be greater than 0')
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  paymentMode: number()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .required('Payment Method ' + help.REQUIRED)
    .typeError('Payment Method ' + help.REQUIRED),
  bankAccountId: string().required('Bank Account ' + help.REQUIRED),
  transactionReference: string().required(
    'Transaction Reference ' + help.REQUIRED
  ),
});

export const productionValidation = object({
  productId: string().required(help.PRODUCT_REQUIRED),
  producedQuantity: number()
    .required('Produced Quantity ' + help.REQUIRED)
    .min(1, help.PRODUCED_QUANTITY_MIN)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
  damagedQuantity: number()
    .required('Damaged Quantity ' + help.REQUIRED)
    .min(0, help.DAMAGED_QUANTITY_MIN)
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT)
    .test(
      'damaged-quantity-check',
      help.DAMAGED_QUANTITY_EXCEEDS_PRODUCED,
      function (value) {
        const { producedQuantity } = this.parent;
        if (value === undefined || producedQuantity === undefined) {
          return true;
        }
        return value <= producedQuantity;
      }
    ),
  productionDate: string()
    .nullable()
    .required('Production Date ' + help.REQUIRED)
    .test('not-future-date', help.DATE_CANNOT_BE_FUTURE, function (value) {
      if (!value) return true;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return selectedDate <= today;
    }),
});

export const orderFilterValidation = object({
  vendorPicker: string().nullable(),
  statusPicker: string().nullable(),
  startDate: string().nullable(),
  endDate: string()
    .nullable()
    .test(
      'end-date-after-start-date',
      'End date must be the same as or later than the start date.',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        return new Date(value) >= new Date(startDate);
      }
    ),
});

export const voucherFilterValidation = object({
  voucherTypePicker: string().nullable(),
  statusPicker: string().nullable(),
  startDate: string().nullable(),
  endDate: string()
    .nullable()
    .test(
      'end-date-after-start-date',
      'End date must be the same as or later than the start date.',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        return new Date(value) >= new Date(startDate);
      }
    ),
});

// Voucher Payment Validations - Dynamic schema that handles all payment modes
export const voucherPaymentDynamicValidation = object({
  receiverName: string()
    .trim()
    .required('Received By ' + help.REQUIRED),
  paymentDate: string()
    .required('Payment Date ' + help.REQUIRED)
    .test(
      'not-future-date',
      help.PAYMENT_DATE_CANNOT_BE_FUTURE,
      function (value) {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return selectedDate <= today;
      }
    ),
  amount: string()
    .required('Amount ' + help.REQUIRED)
    .test('is-valid-amount', 'Amount must be greater than 0', function (value) {
      if (!value) return false;
      const numValue = parseFloat(value.replace(/,/g, ''));
      return !isNaN(numValue) && numValue > 0;
    }),
  paymentMode: number()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .required('Payment Method ' + help.REQUIRED)
    .typeError('Payment Method ' + help.REQUIRED),
  chequeNumber: string().when('paymentMode', {
    is: 2,
    then: (schema) => schema.required('Cheque Number ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  chequeDate: string().when('paymentMode', {
    is: 2,
    then: (schema) => schema.required('Cheque Date ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  bankAccountId: string().when('paymentMode', {
    is: (val: number) => val === 2 || val === 3,
    then: (schema) => schema.required('Bank Account ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  transactionReference: string().when('paymentMode', {
    is: 3,
    then: (schema) => schema.required('Transaction Reference ' + help.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const voucherValidationSchema = object({
  voucherTypeId: string()
    .trim()
    .required('Voucher Type ' + help.REQUIRED),
  payee: string()
    .trim()
    .required('Payee ' + help.REQUIRED),
  description: string()
    .trim()
    .required('Description ' + help.REQUIRED),
  billNumber: string()
    .trim()
    .required('Bill Number ' + help.REQUIRED),
  billAmount: number()
    .required('Bill Amount ' + help.REQUIRED)
    .positive('Bill Amount must be greater than 0')
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.trim() === '' ? undefined : value;
      }
      return originalValue === null || originalValue === undefined
        ? undefined
        : value;
    })
    .typeError(help.INVALID_FORMAT),
});

// export const purchaseOrderStatusUpdateValidation = object({
//   purchaseOrderItems: array().of(
//     object({
//       id: string(),
//       rawMaterialId: string(),
//       name: string(),
//       requestedQty: string(),
//       approvedQty: number()
//         .transform((value, originalValue) => {
//           if (
//             originalValue === '' ||
//             originalValue === null ||
//             originalValue === undefined
//           ) {
//             return undefined;
//           }
//           return value;
//         })
//         .when('status', {
//           is: 'APPROVED',
//           then: (schema) =>
//             schema
//               .typeError('Approved quantity is Required')
//               .required('Approved quantity is Required')
//               .moreThan(0, 'Approved quantity must be greater than 0'),
//           otherwise: (schema) => schema.nullable(),
//         }),
//     })
//   ),
// });
export const purchaseOrderStatusUpdateValidation = object({
  purchaseOrderItems: array().of(
    object().shape({
      rawMaterialId: string().required('Product is required'),
      approvedQty: number()
        .min(1, 'Quantity must be at least 1')
        .required('Quantity is required'),
    })
  ),
});

export const productionFilterValidation = object({
  productPicker: string().nullable(),
  startDate: string().nullable(),
  endDate: string()
    .nullable()
    .test(
      'end-date-after-start-date',
      'End date must be the same as or later than the start date.',
      function (value) {
        const { startDate } = this.parent;
        if (!startDate || !value) return true;
        return new Date(value) >= new Date(startDate);
      }
    ),
});
